import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Activity, Calculator, CalendarClock } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './TdeeCalculatorPage/components/CalculationForm';
import ResultsOverview from './TdeeCalculatorPage/components/ResultsOverview';
import NutritionRatios from './TdeeCalculatorPage/components/NutritionRatios';
import QuickAdjustmentSidebar from './TdeeCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './TdeeCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './TdeeCalculatorPage/components/TipsSidebar';
import InfoContent from './TdeeCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  gender: 'male',
  age: '30',
  weight: '75',
  height: '175',
  activityLevel: 'moderate',
  goal: 'maintain',
  formula: 'mifflin',
  measurementSystem: 'metric', // 'metric' или 'imperial'
  bodyFat: '',
  includeBodyFat: false,
  includeAdvancedOptions: false,
  dailySteps: '7500',
  workoutDays: '3',
  workoutIntensity: 'moderate',
  stress: 'low',
  sleepHours: '7',
  adjustForAge: true,
  specificDiet: 'none',
  mealFrequency: '3',
  weightChange: '0.5', // кг в неделю для похудения/набора
  dietHistory: 'none'
};

// Определение уровней активности
export const activityLevels = [
  { id: 'sedentary', name: 'Сидячий образ жизни', description: 'Офисная работа, минимальная физическая активность', factor: 1.2 },
  { id: 'light', name: 'Легкая активность', description: '1-2 тренировки в неделю, работа с небольшой активностью', factor: 1.375 },
  { id: 'moderate', name: 'Умеренная активность', description: '3-5 тренировок в неделю, активная работа', factor: 1.55 },
  { id: 'high', name: 'Высокая активность', description: '6-7 интенсивных тренировок в неделю', factor: 1.725 },
  { id: 'very_high', name: 'Очень высокая активность', description: 'Профессиональный спорт, физическая работа, 2 тренировки в день', factor: 1.9 }
];

// Определение целей
export const goals = [
  { id: 'lose_fast', name: 'Интенсивное похудение', description: 'Дефицит 20% от TDEE', factor: 0.8 },
  { id: 'lose', name: 'Плавное похудение', description: 'Дефицит 10% от TDEE', factor: 0.9 },
  { id: 'maintain', name: 'Поддержание веса', description: 'Ваша суточная норма калорий', factor: 1.0 },
  { id: 'gain', name: 'Набор веса/мышечной массы', description: 'Профицит 10% от TDEE', factor: 1.1 },
  { id: 'gain_fast', name: 'Активный набор массы', description: 'Профицит 20% от TDEE', factor: 1.2 }
];

// Определение формул расчета
export const formulas = [
  { id: 'mifflin', name: 'Миффлина-Сан Жеора', description: 'Самая современная и точная формула для большинства людей (рекомендуется)' },
  { id: 'harris', name: 'Харриса-Бенедикта', description: 'Классическая формула, немного завышающая результаты' },
  { id: 'katch', name: 'Катч-МакАрдла', description: 'Использует процент жира в организме для более точного расчета' }
];

/**
 * TdeeCalculatorPage Component - Полный калькулятор суточной потребности в калориях (TDEE)
 * с различными формулами и дополнительными параметрами.
 */
const TdeeCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('tdee-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор TDEE | Расчет суточной нормы калорий | Gensio';
  const metaDescription = tool?.metaDescription || 'Точный расчет суточной потребности в калориях (TDEE) с учетом пола, возраста, веса и уровня активности. Рекомендации по питанию для разных целей.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [nutritionPlan, setNutritionPlan] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedProfiles, setSavedProfiles] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных профилей из localStorage ---
  useEffect(() => {
    const savedProfilesData = localStorage.getItem('savedTdeeProfiles');
    if (savedProfilesData) {
      try {
        setSavedProfiles(JSON.parse(savedProfilesData));
      } catch (e) {
        console.error("Ошибка загрузки сохраненных профилей:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'weight-loss':
          newState.goal = 'lose';
          break;
        case 'muscle-gain':
          newState.goal = 'gain';
          break;
        case 'athlete':
          newState.activityLevel = 'high';
          newState.includeBodyFat = true;
          break;
        case 'elderly':
          newState.adjustForAge = true;
          newState = { ...newState, age: '65', activityLevel: 'light' };
          break;
        case 'teenager':
          newState.adjustForAge = true;
          newState = { ...newState, age: '16', activityLevel: 'moderate' };
          break;
        case 'pregnant':
          newState.gender = 'female';
          newState.includeAdvancedOptions = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setNutritionPlan(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение профиля в localStorage
  const saveProfile = (name) => {
    if (!results) return;

    const newSavedProfile = {
      id: Date.now().toString(),
      name: name || `Профиль ${formData.gender === 'male' ? 'мужчина' : 'женщина'}, ${formData.age} лет, ${formData.weight} кг`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedProfiles = [...savedProfiles, newSavedProfile];
    setSavedProfiles(updatedSavedProfiles);
    localStorage.setItem('savedTdeeProfiles', JSON.stringify(updatedSavedProfiles));

    return newSavedProfile;
  };

  // Удаление сохраненного профиля
  const deleteSavedProfile = (id) => {
    const updatedSavedProfiles = savedProfiles.filter(profile => profile.id !== id);
    setSavedProfiles(updatedSavedProfiles);
    localStorage.setItem('savedTdeeProfiles', JSON.stringify(updatedSavedProfiles));
  };

  // Загрузка сохраненного профиля
  const loadSavedProfile = (id) => {
    const profileToLoad = savedProfiles.find(profile => profile.id === id);
    if (profileToLoad) {
      setFormData(profileToLoad.formData);
      setResults(profileToLoad.results);
      if (profileToLoad.results) {
        // Генерация плана питания на основе загруженных результатов
        generateNutritionPlan(profileToLoad.results);
      }
    }
  };

  // Генерация плана питания на основе результатов TDEE
  const generateNutritionPlan = (tdeeResults) => {
    if (!tdeeResults) return;

    const { tdee, goal } = tdeeResults;
    
    // Коэффициенты макронутриентов в зависимости от цели
    let proteinRatio, fatRatio, carbRatio;
    
    if (goal.includes('lose')) {
      // Похудение: повышенный белок, умеренные жиры, пониженные углеводы
      proteinRatio = 0.35; // 35% от калорий
      fatRatio = 0.30; // 30% от калорий
      carbRatio = 0.35; // 35% от калорий
    } else if (goal.includes('gain')) {
      // Набор массы: высокий белок, умеренные жиры, высокие углеводы
      proteinRatio = 0.30; // 30% от калорий
      fatRatio = 0.25; // 25% от калорий
      carbRatio = 0.45; // 45% от калорий
    } else {
      // Поддержание: сбалансированное распределение
      proteinRatio = 0.30; // 30% от калорий
      fatRatio = 0.30; // 30% от калорий
      carbRatio = 0.40; // 40% от калорий
    }

    // Калории на грамм макронутриентов
    const caloriesPerGramProtein = 4;
    const caloriesPerGramCarb = 4;
    const caloriesPerGramFat = 9;

    // Расчет граммов макронутриентов
    const proteinCalories = tdee * proteinRatio;
    const fatCalories = tdee * fatRatio;
    const carbCalories = tdee * carbRatio;

    const proteinGrams = Math.round(proteinCalories / caloriesPerGramProtein);
    const fatGrams = Math.round(fatCalories / caloriesPerGramFat);
    const carbGrams = Math.round(carbCalories / caloriesPerGramCarb);

    // Количество приемов пищи
    const meals = parseInt(formData.mealFrequency) || 3;

    // Распределение по приемам пищи
    const mealDistribution = [];
    
    if (meals === 3) {
      // Стандартное распределение для 3 приемов пищи
      mealDistribution.push(
        { name: 'Завтрак', percentage: 0.3 },
        { name: 'Обед', percentage: 0.45 },
        { name: 'Ужин', percentage: 0.25 }
      );
    } else if (meals === 4) {
      // Распределение для 4 приемов пищи
      mealDistribution.push(
        { name: 'Завтрак', percentage: 0.25 },
        { name: 'Перекус', percentage: 0.15 },
        { name: 'Обед', percentage: 0.35 },
        { name: 'Ужин', percentage: 0.25 }
      );
    } else if (meals === 5) {
      // Распределение для 5 приемов пищи
      mealDistribution.push(
        { name: 'Завтрак', percentage: 0.25 },
        { name: 'Перекус 1', percentage: 0.10 },
        { name: 'Обед', percentage: 0.30 },
        { name: 'Перекус 2', percentage: 0.15 },
        { name: 'Ужин', percentage: 0.20 }
      );
    } else if (meals === 6) {
      // Распределение для 6 приемов пищи
      mealDistribution.push(
        { name: 'Завтрак', percentage: 0.20 },
        { name: 'Перекус 1', percentage: 0.10 },
        { name: 'Обед', percentage: 0.25 },
        { name: 'Перекус 2', percentage: 0.15 },
        { name: 'Ужин', percentage: 0.20 },
        { name: 'Вечерний перекус', percentage: 0.10 }
      );
    } else {
      // Равномерное распределение для любого другого количества приемов пищи
      const percentage = 1 / meals;
      for (let i = 0; i < meals; i++) {
        mealDistribution.push({
          name: `Прием пищи ${i + 1}`,
          percentage: percentage
        });
      }
    }

    // Составление плана питания с учетом выбранной диеты
    const dietType = formData.specificDiet;
    let restrictedFoods = [];
    let recommendedFoods = [];

    switch (dietType) {
      case 'keto':
        restrictedFoods = ['хлеб', 'макароны', 'сахар', 'фрукты', 'рис', 'картофель'];
        recommendedFoods = ['мясо', 'сыр', 'авокадо', 'орехи', 'яйца', 'оливковое масло', 'рыба'];
        // Для кето корректируем макросы
        const ketoProteinGrams = Math.round(tdee * 0.25 / caloriesPerGramProtein);
        const ketoFatGrams = Math.round(tdee * 0.70 / caloriesPerGramFat);
        const ketoCarbGrams = Math.round(tdee * 0.05 / caloriesPerGramCarb);
        setNutritionPlan({
          calories: tdee,
          macros: {
            protein: ketoProteinGrams,
            fat: ketoFatGrams,
            carbs: ketoCarbGrams
          },
          mealPlan: mealDistribution,
          restrictedFoods,
          recommendedFoods
        });
        break;
      
      case 'lowfat':
        restrictedFoods = ['масло', 'жирное мясо', 'сыр', 'орехи', 'жареная пища'];
        recommendedFoods = ['курица без кожи', 'обезжиренные молочные продукты', 'бобовые', 'фрукты', 'овощи', 'цельнозерновые'];
        // Для низкожировой диеты корректируем макросы
        const lowfatProteinGrams = Math.round(tdee * 0.30 / caloriesPerGramProtein);
        const lowfatFatGrams = Math.round(tdee * 0.15 / caloriesPerGramFat);
        const lowfatCarbGrams = Math.round(tdee * 0.55 / caloriesPerGramCarb);
        setNutritionPlan({
          calories: tdee,
          macros: {
            protein: lowfatProteinGrams,
            fat: lowfatFatGrams,
            carbs: lowfatCarbGrams
          },
          mealPlan: mealDistribution,
          restrictedFoods,
          recommendedFoods
        });
        break;
      
      case 'vegetarian':
        restrictedFoods = ['мясо', 'птица', 'рыба'];
        recommendedFoods = ['яйца', 'молочные продукты', 'тофу', 'орехи', 'семена', 'бобовые', 'темпе', 'сейтан'];
        setNutritionPlan({
          calories: tdee,
          macros: {
            protein: proteinGrams,
            fat: fatGrams,
            carbs: carbGrams
          },
          mealPlan: mealDistribution,
          restrictedFoods,
          recommendedFoods
        });
        break;
      
      case 'vegan':
        restrictedFoods = ['мясо', 'птица', 'рыба', 'яйца', 'молочные продукты', 'мед'];
        recommendedFoods = ['тофу', 'темпе', 'орехи', 'семена', 'бобовые', 'цельнозерновые', 'овощи', 'фрукты', 'водоросли (источник B12)'];
        setNutritionPlan({
          calories: tdee,
          macros: {
            protein: proteinGrams,
            fat: fatGrams,
            carbs: carbGrams
          },
          mealPlan: mealDistribution,
          restrictedFoods,
          recommendedFoods
        });
        break;
      
      default:
        // Для обычной/сбалансированной диеты
        recommendedFoods = ['нежирное мясо', 'рыба', 'яйца', 'молочные продукты', 'цельнозерновые', 'фрукты', 'овощи', 'бобовые', 'орехи', 'семена'];
        setNutritionPlan({
          calories: tdee,
          macros: {
            protein: proteinGrams,
            fat: fatGrams,
            carbs: carbGrams
          },
          mealPlan: mealDistribution,
          restrictedFoods,
          recommendedFoods
        });
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Activity className="w-8 h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор суточной потребности в калориях (TDEE)'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              generateNutritionPlan={generateNutritionPlan}
              activityLevels={activityLevels}
              goals={goals}
              formulas={formulas}
              saveProfile={saveProfile}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <NutritionRatios 
                  nutritionPlan={nutritionPlan} 
                  results={results} 
                />
              </>
            )}

            {/* --- Информационный контент о питании и TDEE --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              activityLevels={activityLevels}
              goals={goals}
            />

            {/* --- Панель сравнения профилей --- */}
            <ComparisonSidebar 
              savedProfiles={savedProfiles}
              deleteSavedProfile={deleteSavedProfile}
              loadSavedProfile={loadSavedProfile}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default TdeeCalculatorPage;